import * as React from 'react';
import { useFormikContext, useField } from 'formik';

import { PasswordInput } from 'security-app/components/common';

export type PasswordType = {
  keep_value?: boolean;
  is_set?: boolean;
  set_value?: string;
};

type Props = Pick<
  React.ComponentProps<typeof PasswordInput>,
  'label' | 'action' | 'help' | 'placeholder' | 'disabled' | 'required' | 'hasPassword'
> & { field: any };

function FormikPasswordInput({ field, label, action, placeholder, help, required, disabled, hasPassword }: Props) {
  const [_f, { touched, error }] = useField({ name: field.name });
  const { setFieldValue, setFieldTouched } = useFormikContext();
  const [localValue, setLocalValue] = React.useState<string | boolean>(
    field.value.keep_value || field.value.is_set || field.value.set_value,
  );

  React.useEffect(() => {
    if (field.value.is_set) {
      setFieldValue(field.name, { keep_value: true });
    }
  }, [field.name, field.value, setFieldValue]);

  const updateValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = e;
    setLocalValue(target.value);
    setFieldTouched(target.name, true);
    field.onChange({
      ...e,
      target: {
        ...target,
        name: field.name,
        value: typeof target.value === 'boolean' ? { keep_value: target.value } : { set_value: target.value },
      },
    });
  };

  const onResetValue = React.useCallback(() => {
    setFieldValue(field.name, { set_value: '' });
    setFieldTouched(field.name, true);
  }, [field.name, setFieldTouched, setFieldValue]);

  const onCancelResetValue = React.useCallback(() => {
    setFieldValue(field.name, { keep_value: hasPassword });
  }, [setFieldValue, field.name, hasPassword]);

  return (
    <PasswordInput
      name={field.name}
      label={label}
      action={action}
      placeholder={placeholder}
      value={localValue}
      help={touched && !!error ? null : help}
      error={touched && !!error ? error : ''}
      onChange={updateValue}
      onBlur={updateValue}
      onResetPassword={onResetValue}
      onCancelReset={onCancelResetValue}
      hasPassword={hasPassword}
      required={required}
      disabled={disabled}
    />
  );
}

export default FormikPasswordInput;
